import React from 'react';

/**
 * <Picture> — drop-in replacement for <img loading="lazy" decoding="async"> that:
 *   • Serves WebP if browser supports it (modern: ~96% global coverage)
 *   • Falls back to original PNG/JPG for legacy browsers
 *   • Lazy-loads by default (set `loading="eager"` to override for above-the-fold)
 *   • Sets fetchpriority for LCP images (the hero) when `priority` prop is used
 *
 * Usage:
 *   <Picture src="/images/home_hero.png" alt="..." className="..." />
 *   <Picture src="/images/home_hero.png" priority alt="..." />  // for hero
 */
export default function Picture({
    src,
    alt = '',
    className,
    priority = false,
    loading,
    ...rest
}) {
    // Replace .png / .jpg / .jpeg → .webp for modern source
    const webpSrc = src && /\.(png|jpe?g)$/i.test(src)
        ? src.replace(/\.(png|jpe?g)$/i, '.webp')
        : src;

    // Above-the-fold images: load eagerly, low-latency
    const effectiveLoading = loading || (priority ? 'eager' : 'lazy');
    const effectivePriority = priority ? 'high' : 'auto';

    return (
        <picture>
            <source srcSet={webpSrc} type="image/webp" />
            <img
                src={src}
                alt={alt}
                className={className}
                loading={effectiveLoading}
                fetchPriority={effectivePriority}
                decoding="async"
                {...rest}
            />
        </picture>
    );
}
